import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MDBIcon, MDBInput, MDBSelect, MDBSpinner, MDBTextArea } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import BilingualEditor from '../../components-dev-admin/shared/BilingualEditor';
import SlugField from '../../components/toolbox/SlugField';
import { useBreadcrumbContext } from '../../components/breadcrumb/BreadcrumbContext';
import { getLocalizedText } from '../../utils/localizedText';
import { pickSourceText } from '../../utils/slugify';
import {
  formatLegacyData,
  getLanguageText,
  getLocalized,
  mediathekImageUrl,
  publicMediaPath,
} from '../../utils/mediathek';
import useMediaSeriesData from './hooks/useMediaSeriesData';
import useMediathekUpload from './hooks/useMediathekUpload';
import {
  ConfirmModal,
  ErrorList,
  FlashToast,
  StatusBadge,
  TypeBadge,
  selectValue,
  seriesTypeIcon,
  useFlashToast,
} from './MediathekShared';
import {
  CharCount,
  EditorColumns,
  EditorHeader,
  EditorMain,
  EditorSection,
  EditorShell,
  LanguageSwitch,
  SidePanel,
  StatusToggle,
  StickyActionBar,
  revealElement,
  useBeforeUnload,
  useDirtyState,
  useSaveShortcut,
} from './MediathekEditorLayout';
import { ImageSlot } from './MediaSlots';
import SeriesItemsSection from './series-editor/SeriesItemsSection';

const LIST_PATH = '/admin/mediathek';
const ERRORS_ID = 'me-errors';
/** Marker of "industry = the default of a new series" in the dirty-tracking value */
const DEFAULT_INDUSTRY = '__default__';
/**
 * Cover column of the identity block: ~200px beside the texts; alone on its line
 * (narrow screens) it grows to full width, capped at 280px.
 */
const COVER_COLUMN_STYLE = { flex: '1 1 200px', maxWidth: '280px', minWidth: '180px' };
/** Text column: takes (almost) all remaining space, wraps below the cover when narrower than 18rem. */
const TEXT_COLUMN_STYLE = { flex: '1000 1 18rem', minWidth: 0 };

/** Runs after the next paint (e.g. once the error list of a failed save is rendered). */
const afterPaint = (fn) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) window.requestAnimationFrame(fn);
  else setTimeout(fn, 0);
};

/** MultiLangString list without empty texts / __typename (typing and deleting again is not a change). */
const cleanLocalized = (list) => (Array.isArray(list)
  ? list
    .filter((entry) => entry && typeof entry.text === 'string' && entry.text.trim() !== '' && entry.text !== '<p></p>')
    .map((entry) => ({ language: entry.language, text: entry.text }))
  : []);

/**
 * Value compared by the dirty tracking. A new series gets its default industry
 * ("cp") after the industries have loaded - that automatic default is no change.
 */
function dirtyTrackingValue(series, { isCreate, defaultIndustryId }) {
  let industryId = series.industryId ?? null;
  if (isCreate && (series.industryId === undefined || series.industryId === defaultIndustryId)) industryId = DEFAULT_INDUSTRY;
  return {
    ...series,
    title: cleanLocalized(series.title),
    excerpt: cleanLocalized(series.excerpt),
    description: cleanLocalized(series.description),
    industryId,
  };
}

export default function EditMediaSeries() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setDynamicLabel } = useBreadcrumbContext();
  const {
    series,
    setSeries,
    setField,
    setLocalizedField,
    loadedType,
    items,
    hasItems,
    typeLocked,
    publishedCount,
    industries,
    isLoading,
    isLoadingItems,
    loadError,
    isSaving,
    isReordering,
    errors,
    loadVersion,
    save,
    deleteSeries,
    reorderItems,
    reloadItems,
  } = useMediaSeriesData(seriesId);
  const { getUpload, uploadImage } = useMediathekUpload('EditMediaSeries');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lang, setLang] = useState('de');
  const [saveAttempted, setSaveAttempted] = useState(false);
  // 'Item saved' / 'Item deleted' when coming back from the item form
  const [toast, setToast] = useFlashToast();

  const isCreate = !seriesId;
  const titleDe = getLanguageText(series.title, 'de');
  const titleEn = getLanguageText(series.title, 'en');
  const displayTitle = getLocalized(series.title, i18n.language);
  const coverUpload = getUpload('cover');
  // The hook locks the type while items load or when loading them failed; without items and
  // without a running load, the lock means the load failed
  const itemsLoadFailed = typeLocked && !hasItems && !isLoadingItems;

  useEffect(() => {
    if (seriesId && displayTitle) setDynamicLabel(':seriesId', displayTitle);
  }, [seriesId, displayTitle, setDynamicLabel]);

  const industryOptions = useMemo(() => [
    { text: t('All industries'), value: '' },
    ...[...industries]
      .map((industry) => ({ text: getLocalizedText(industry.name) || industry.handle || industry.id, value: industry.id }))
      .sort((a, b) => a.text.localeCompare(b.text, 'de')),
  ], [industries, t]);

  const defaultIndustryId = useMemo(() => {
    const cp = industries.find((industry) => industry.handle === 'cp');
    return cp ? cp.id : null;
  }, [industries]);

  const trackedValue = useMemo(
    () => dirtyTrackingValue(series, { isCreate, defaultIndustryId }),
    [series, isCreate, defaultIndustryId],
  );
  const { isDirty } = useDirtyState(trackedValue, isCreate ? 'create' : `edit-${loadVersion}`, { warnOnUnload: !isCreate });
  // Create mode: warn before leaving once the form has a title
  useBeforeUnload(isCreate && Boolean(titleDe.trim() || titleEn.trim()));

  const handleSave = async () => {
    if (isSaving || coverUpload.busy) return;
    setSaveAttempted(true);
    const result = await save();
    if (result) {
      navigate(LIST_PATH, { state: { toast: 'Series saved' } });
      return;
    }
    if (!titleDe.trim()) setLang('de');
    afterPaint(() => revealElement(ERRORS_ID));
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const ok = await deleteSeries();
    setIsDeleting(false);
    setDeleteOpen(false);
    if (ok) {
      navigate(LIST_PATH, { state: { toast: 'Series deleted' } });
      return;
    }
    afterPaint(() => revealElement(ERRORS_ID));
  };

  const handleCoverPick = async (file) => {
    const image = await uploadImage(file, 'cover');
    // Functional update: keeps an alternative text typed while the upload was running
    if (image) setSeries((prev) => ({ ...prev, coverImage: { ...image, alt: prev.coverImage?.alt || '' } }));
  };

  useSaveShortcut(handleSave, !isLoading && !loadError && !isSaving && !coverUpload.busy && !deleteOpen);

  if (isLoading) {
    return (
      <EditorShell>
        <div className="text-center py-5">
          <MDBSpinner color="primary" role="status">
            <span className="visually-hidden">{t('Loading...')}</span>
          </MDBSpinner>
        </div>
      </EditorShell>
    );
  }

  if (loadError) {
    return (
      <EditorShell>
        <div className="card">
          <div className="card-body text-center py-5">
            <MDBIcon fas icon="exclamation-triangle" size="3x" className="text-danger mb-3" aria-hidden="true" />
            <p className="text-danger" role="alert">{t(loadError)}</p>
            <Link to={LIST_PATH} className="btn btn-secondary">{t('Back')}</Link>
          </div>
        </div>
      </EditorShell>
    );
  }

  const legacyText = formatLegacyData(series.legacyData);
  const publicPath = publicMediaPath(series.slug);
  const titleInvalid = saveAttempted && !titleDe.trim();
  const activeTitle = getLanguageText(series.title, lang);
  const activeExcerpt = getLanguageText(series.excerpt, lang);
  const langShort = lang.toUpperCase();

  const header = (
    <EditorHeader
      thumbnailUrl={series.coverImage?.fileName ? mediathekImageUrl(series.coverImage.fileName) : ''}
      thumbnailAlt={series.coverImage?.alt || ''}
      thumbnailIcon={seriesTypeIcon(series.type)}
      eyebrow={(
        <>
          <TypeBadge type={series.type} />
          <span>{t('Series')}</span>
        </>
      )}
      title={titleDe || titleEn || t('New series')}
      meta={[
        publicPath && (
          <>
            <MDBIcon fas icon="link" aria-hidden="true" />
            {publicPath}
          </>
        ),
        seriesId && !isLoadingItems && !itemsLoadFailed
          && t('{{itemCount}} items, {{publishedCount}} published', { itemCount: items.length, publishedCount }),
      ]}
      status={<StatusBadge status={series.status} />}
      actions={seriesId && (
        <Link to={`/admin/mediathek/series/${seriesId}/items/create`} className="btn btn-primary btn-sm">
          <MDBIcon fas icon="plus" className="me-2" aria-hidden="true" />
          {t('New item')}
        </Link>
      )}
    />
  );

  const aside = (
    <>
      <SidePanel id="series-publishing" title={t('Publishing')}>
        <div className="d-flex flex-column gap-3">
          <div>
            <StatusToggle value={series.status} onChange={(value) => setField('status', value)} disabled={isSaving} />
            <div className="me-help">{t('Only published series (and their published items) are shown on the website.')}</div>
          </div>
          <div>
            {industries.length > 0 ? (
              <MDBSelect
                label={t('Industry')}
                data={industryOptions}
                value={series.industryId || ''}
                onChange={(entry) => {
                  const value = selectValue(entry);
                  if (value === undefined) return;
                  const next = value || null;
                  if (next !== (series.industryId ?? null)) setField('industryId', next);
                }}
              />
            ) : (
              <MDBInput id="series-industry" label={t('Industry')} value={series.industryId || t('All industries')} disabled readOnly />
            )}
          </div>
          <div>
            <MDBInput
              id="series-sort-key"
              label={t('Sort key')}
              type="number"
              step="1"
              inputMode="numeric"
              value={series.sortKey}
              aria-describedby="series-sort-key-help"
              onChange={(e) => setField('sortKey', e.target.value)}
            />
            <div className="me-help" id="series-sort-key-help">{t('Ascending; empty = at the end')}</div>
          </div>
        </div>
      </SidePanel>

      <SidePanel id="series-format" title={t('Format')}>
        <div className="d-flex flex-column gap-3">
          <div>
            <MDBSelect
              label={t('Type')}
              data={[
                { text: t('Podcast'), value: 'podcast' },
                { text: t('Video'), value: 'video' },
              ]}
              value={series.type}
              disabled={typeLocked}
              onChange={(entry) => {
                const value = selectValue(entry);
                if (value !== undefined && value !== series.type) setField('type', value);
              }}
            />
            {hasItems && (
              <div className="me-help">
                <MDBIcon fas icon="lock" className="me-1" aria-hidden="true" />
                {t('The type cannot be changed while the series has items')}
                {loadedType && loadedType !== series.type && ` (${loadedType})`}
              </div>
            )}
          </div>
          <MDBInput
            id="series-host"
            label={t('Host / producer')}
            value={series.host}
            onChange={(e) => setField('host', e.target.value)}
          />
        </div>
      </SidePanel>

      <SidePanel id="series-address" title={t('Web address')}>
        <div style={{ marginBottom: '-1rem' }}>
          <SlugField
            entity="mediaSeries"
            entityId={seriesId}
            value={series.slug}
            history={series.slugHistory}
            sourceText={pickSourceText(series.title)}
            helpText={t('Generated from the German title on save when left empty')}
            disabled={isSaving}
            onChange={(slug) => setField('slug', slug)}
          />
        </div>
      </SidePanel>

      {/* Legacy data (read only, collapsed) */}
      {(legacyText || series.legacyKey) && (
        <SidePanel>
          <details className="me-legacy">
            <summary>{t('Legacy data (read only)')}</summary>
            {series.legacyKey && (
              <div className="small mt-2">{t('Legacy key')}: <code>{series.legacyKey}</code></div>
            )}
            {legacyText && (
              <pre className="small border rounded p-2 mt-2 mb-0" style={{ maxHeight: '400px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {legacyText}
              </pre>
            )}
          </details>
        </SidePanel>
      )}
    </>
  );

  return (
    <EditorShell>
      {header}

      <div id={ERRORS_ID}>
        <ErrorList errors={errors} />
      </div>

      <EditorColumns aside={aside} asideLabel={t('Series settings')}>
        <EditorMain>
          <EditorSection
            id="series-texts"
            icon="align-left"
            title={t('Title & texts')}
            tools={(
              <LanguageSwitch
                value={lang}
                onChange={setLang}
                filled={{ de: Boolean(titleDe.trim()), en: Boolean(titleEn.trim()) }}
                invalid={{ de: titleInvalid }}
              />
            )}
          >
            {/* Identity block: square cover beside title + teaser; wraps below ~34rem (cover then up to 280px wide) */}
            <div className="d-flex flex-wrap align-items-start" style={{ gap: '1rem 1.5rem' }}>
              <div style={COVER_COLUMN_STYLE}>
                <ImageSlot
                  variant="tile"
                  aspect="1/1"
                  label={t('Cover image')}
                  image={series.coverImage}
                  upload={coverUpload}
                  onPick={handleCoverPick}
                  onRemove={() => setField('coverImage', null)}
                  onAltChange={(alt) => setField('coverImage', { ...series.coverImage, alt })}
                  hint={t('JPG or PNG, max. 3 MB')}
                  disabled={isSaving}
                />
              </div>
              <div style={TEXT_COLUMN_STYLE}>
                <div className="mb-3">
                  <MDBInput
                    key={`title-${lang}`}
                    id={`series-title-${lang}`}
                    size="lg"
                    label={lang === 'de' ? `${t('Title German')} *` : t('Title English')}
                    value={activeTitle}
                    aria-invalid={(lang === 'de' && titleInvalid) || undefined}
                    aria-required={lang === 'de' || undefined}
                    onChange={(e) => setLocalizedField('title', lang, e.target.value)}
                  />
                </div>
                <MDBTextArea
                  key={`excerpt-${lang}`}
                  id={`series-excerpt-${lang}`}
                  label={`${t('Excerpt')} (${langShort})`}
                  rows={6}
                  value={activeExcerpt}
                  onChange={(e) => setLocalizedField('excerpt', lang, e.target.value)}
                />
                <CharCount text={activeExcerpt} />
              </div>
            </div>
            {/* The section's language switch also drives the description editor (no own tabs).
                BilingualEditor ends with mb-4; the section padding already spaces the bottom. */}
            <div className="mt-4" style={{ marginBottom: '-1.5rem' }}>
              <BilingualEditor
                key={`series-description-${loadVersion}`}
                title="Description"
                language={lang}
                hideLanguageTabs
                contentDe={getLanguageText(series.description, 'de')}
                contentEn={getLanguageText(series.description, 'en')}
                onChangeDe={(html) => setLocalizedField('description', 'de', html)}
                onChangeEn={(html) => setLocalizedField('description', 'en', html)}
              />
            </div>
          </EditorSection>
        </EditorMain>
      </EditorColumns>

      {seriesId && (
        <SeriesItemsSection
          seriesId={seriesId}
          seriesType={series.type}
          items={items}
          publishedCount={publishedCount}
          isLoadingItems={isLoadingItems}
          isReordering={isReordering}
          loadFailed={itemsLoadFailed}
          onReorder={reorderItems}
          onRetry={reloadItems}
        />
      )}

      <StickyActionBar
        mode={isCreate ? 'create' : 'edit'}
        isDirty={isDirty}
        uploadBusy={coverUpload.busy}
        errorCount={errors.length}
        onErrorsClick={() => revealElement(ERRORS_ID)}
        onDelete={() => setDeleteOpen(true)}
        onBack={() => navigate(LIST_PATH)}
        onSave={handleSave}
        saving={isSaving}
        deleteDisabled={isDeleting}
        labels={{ createState: t('New series – not saved yet') }}
      />

      <ConfirmModal
        open={deleteOpen}
        title={t('Delete series')}
        message={hasItems
          ? t('This series still has {{itemCount}} items. Delete them or move them to another series first.', { itemCount: items.length })
          : t('Are you sure you want to delete the series "{{title}}"?', { title: displayTitle })}
        onConfirm={hasItems ? undefined : handleDelete}
        onClose={() => setDeleteOpen(false)}
        busy={isDeleting}
      />
      <FlashToast message={toast} onClose={() => setToast(null)} />
    </EditorShell>
  );
}
